import { useId, useRef, type ComponentProps, type ReactNode } from 'react';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  type ButtonProps,
  type Theme,
} from '@mui/material';
import { designTokens } from '../tokens';
import { PageHeaderActionsProvider } from './PageHeaderActionsContext';
import { dialogActionsStyles, dialogContentStyles, dialogHeadingStyles } from './dialogLayout';
import { useDialogContentOverflow } from './useDialogContentOverflow';
import { HelpPopover } from './HelpPopover';

type DialogFrameProps = ComponentProps<typeof Dialog>;

function DialogFrame(props: DialogFrameProps) {
  return <Dialog {...props} />;
}

export type FormDialogProps = {
  open: boolean;
  /** 닫힌 동안에도 초안·첨부처럼 진행 중인 본문 작업을 유지합니다. */
  keepMounted?: boolean;
  onClose: () => void;
  onSubmit: () => void | Promise<void>;
  title: ReactNode;
  help?: ReactNode;
  headerContent?: ReactNode;
  /** 항상 표시하는 닫기 버튼의 접근성 이름과 Tooltip입니다. */
  closeLabel?: string;
  children: ReactNode;
  cancelLabel: ReactNode;
  /** 전체 폼 닫기와 별개로 이전 단계로 돌아가는 보조 작업입니다. */
  backAction?: { label?: ReactNode; onClick: () => void };
  submitLabel: ReactNode;
  busy?: boolean;
  busyLabel?: ReactNode;
  submitDisabled?: boolean;
  submitColor?: ButtonProps['color'];
  submitTestId?: string;
  maxWidth?: 'xs' | 'sm' | 'md';
  mobilePresentation?: 'full-screen' | 'dialog';
  initialFocus?: 'cancel';
  /** 팝업이 열린 뒤 호출하며, feature가 입력 영역의 초기 초점을 지정할 수 있습니다. */
  onEntered?: () => void;
  'aria-describedby'?: string;
};

export type WorkspaceSurfacePresentation = 'dialog' | 'standalone';

export type WorkspaceSurfaceProps = {
  open: boolean;
  onClose: () => void;
  ariaLabel: string;
  presentation: WorkspaceSurfacePresentation;
  dialogWidth?: 'standard' | 'wide';
  children: ReactNode;
};

/**
 * 긴 작성 흐름을 현재 맥락 위의 넓은 dialog 또는 shell 없는 독립 workspace로 표시합니다.
 * 업무 header와 action, presentation route는 feature가 소유합니다.
 */
export function WorkspaceSurface({
  open,
  onClose,
  ariaLabel,
  presentation,
  dialogWidth = 'wide',
  children,
}: WorkspaceSurfaceProps) {
  const compact = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const dialogInlineInset = designTokens.layout.workspaceDialogInlineInset * 2;
  const dialogBlockInset = designTokens.layout.workspaceDialogBlockInset * 2;
  const dialogMaxWidth = dialogWidth === 'standard'
    ? designTokens.layout.workspaceDialogStandardMaxWidth
    : designTokens.layout.workspaceDialogMaxWidth;

  if (!open) return null;

  if (presentation === 'standalone') {
    return <Box
      component="section"
      aria-label={ariaLabel}
      data-testid="workspace-standalone"
      sx={{
        width: '100%',
        height: '100%',
        minHeight: 0,
        boxSizing: 'border-box',
        p: { xs: 0, sm: `${designTokens.layout.pageGutter}px` },
        bgcolor: designTokens.color.canvas,
      }}
    >
      <Box
        data-testid="workspace-surface"
        sx={{
          width: '100%',
          height: '100%',
          minHeight: 0,
          overflow: 'clip',
          bgcolor: designTokens.color.canvas,
        }}
      >
        <PageHeaderActionsProvider presentation="inline">
          {children}
        </PageHeaderActionsProvider>
      </Box>
    </Box>;
  }

  return (
    <DialogFrame
      open={open}
      onClose={onClose}
      fullScreen={compact}
      fullWidth
      maxWidth={false}
      data-testid="workspace-dialog"
      data-presentation="dialog"
      slotProps={{
        paper: {
          'aria-label': ariaLabel,
          sx: {
            width: compact ? '100%' : `min(${dialogMaxWidth}px, calc(100% - ${dialogInlineInset}px))`,
            height: compact ? '100%' : `calc(100% - ${dialogBlockInset}px)`,
            maxWidth: 'none',
            maxHeight: 'none',
            m: 0,
            // 본문만 스크롤한다. focus 복귀가 고정 작업 공간 자체를 이동시키지 않게 한다.
            overflow: 'clip',
            borderRadius: compact ? 0 : designTokens.radius.dialog,
            boxShadow: compact ? 'none' : designTokens.shadow.overlay,
          },
        },
      }}
    >
      <Box data-testid="workspace-surface" sx={{ width: '100%', height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column', bgcolor: designTokens.color.canvas }}>
        <PageHeaderActionsProvider presentation="inline">
          {children}
        </PageHeaderActionsProvider>
      </Box>
    </DialogFrame>
  );
}

/**
 * 짧은 생성·수정 form을 위한 responsive dialog shell입니다. 제목, 본문 간격, action 배치와
 * 제출 중 닫기 방지를 소유하고, 업무 문구·field·validation은 slot과 callback으로 받습니다.
 */
export function FormDialog({
  open,
  keepMounted = false,
  onClose,
  onSubmit,
  title,
  help,
  headerContent,
  closeLabel = '팝업 닫기',
  children,
  cancelLabel,
  backAction,
  submitLabel,
  busy = false,
  busyLabel = submitLabel,
  submitDisabled = false,
  submitColor = 'primary',
  submitTestId,
  maxWidth = 'sm',
  mobilePresentation = 'full-screen',
  initialFocus,
  onEntered,
  'aria-describedby': describedBy,
}: FormDialogProps) {
  const compact = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const fullScreen = compact && mobilePresentation === 'full-screen';
  const generatedId = useId().replace(/[^a-zA-Z0-9_-]/g, '');
  const titleId = `form-dialog-${generatedId}-title`;
  const cancelButtonRef = useRef<HTMLButtonElement>(null);
  const { contentRef, overflowing } = useDialogContentOverflow();
  const surfacePadding = {
    xs: `${designTokens.layout.surfacePaddingCompact}px`,
    sm: `${designTokens.layout.surfacePadding}px`,
  };
  const requestClose = () => {
    if (!busy) onClose();
  };
  const cancelAction = <Button
    ref={cancelButtonRef} type="button" variant="outlined" color="inherit" disabled={busy} onClick={requestClose}
    sx={backAction ? { flex: { xs: 1, sm: 'initial' } } : undefined}
  >{cancelLabel}</Button>;

  return (
    <DialogFrame
      open={open}
      keepMounted={keepMounted}
      onClose={requestClose}
      aria-labelledby={titleId}
      aria-describedby={describedBy}
      fullScreen={fullScreen}
      fullWidth
      maxWidth={maxWidth}
      slotProps={{
        paper: { sx: {
          m: fullScreen ? 0 : surfacePadding,
          width: fullScreen ? '100%' : {
            xs: `calc(100% - ${designTokens.layout.surfacePaddingCompact * 2}px)`,
            sm: `calc(100% - ${designTokens.layout.dialogInlineInset * 2}px)`,
          },
          maxHeight: fullScreen ? 'none' : {
            xs: `calc(100% - ${designTokens.layout.surfacePaddingCompact * 2}px)`,
            sm: `calc(100% - ${designTokens.layout.dialogBlockInset * 2}px)`,
          },
        } },
        transition: { onEntered: () => {
          // Dialog의 초기 표면 초점과 trigger 기록이 끝난 뒤 안전한 행동으로 이동합니다.
          if (initialFocus === 'cancel') cancelButtonRef.current?.focus();
          onEntered?.();
        } },
      }}
    >
      <Box
        component="form"
        noValidate
        aria-busy={busy}
        onSubmit={(event) => {
          event.preventDefault();
          if (!busy && !submitDisabled) void onSubmit();
        }}
        sx={{ display: 'flex', minHeight: 0, flex: 1, flexDirection: 'column' }}
      >
        <DialogTitle id={`${titleId}-header`} component="div" sx={dialogHeadingStyles()}>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', alignItems: 'center', columnGap: `${designTokens.space.sm}px` }}>
            <Box sx={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: `${designTokens.space.xxs}px` }}>
              <Typography id={titleId} component="h2" variant="h5" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>{title}</Typography>
              {help === undefined ? null : <HelpPopover title={typeof title === 'string' ? title : '입력 안내'}>{help}</HelpPopover>}
            </Box>
            <Tooltip title={closeLabel}>
              <Box component="span" sx={{ display: 'inline-flex' }}>
                <IconButton type="button" aria-label={closeLabel} disabled={busy} onClick={requestClose}>
                  <CloseRoundedIcon />
                </IconButton>
              </Box>
            </Tooltip>
          </Box>
          {headerContent === undefined ? null : <Box sx={{ mt: `${designTokens.layout.surfacePadding}px` }}>{headerContent}</Box>}
        </DialogTitle>
        <DialogContent ref={contentRef} sx={dialogContentStyles({ fill: true })}>
          <Box component="fieldset" disabled={busy} sx={{ m: 0, minWidth: 0, border: 0, p: 0 }}>
            <Stack spacing={3}>{children}</Stack>
          </Box>
        </DialogContent>
        <DialogActions disableSpacing sx={dialogActionsStyles({ stackOnMobile: true, scrollDivider: overflowing })}>
          {backAction ? <Box sx={{ display: 'flex', gap: `${designTokens.space.xs}px` }}>
            {cancelAction}
            <Button type="button" variant="outlined" disabled={busy} onClick={() => { if (!busy) backAction.onClick(); }} sx={{ flex: { xs: 1, sm: 'initial' } }}>
              {backAction.label ?? '이전'}
            </Button>
          </Box> : cancelAction}
          <Button
            type="submit"
            variant="contained"
            color={submitColor}
            data-testid={submitTestId}
            disabled={busy || submitDisabled}
            startIcon={busy ? <CircularProgress aria-hidden size={16} color="inherit" /> : undefined}
          >
            {busy ? busyLabel : submitLabel}
          </Button>
        </DialogActions>
      </Box>
    </DialogFrame>
  );
}
